'use client'

import { paths } from '@/config/paths'
import { EBorrow } from '@/features/eborrow/components/EBorrow'
import { EulerQuickBorrowContainer } from '@/features/euler-quick-borrow/EulerQuickBorrowContainer'
import { useUnifiedEarnOpportunities } from '@/features/unified-earn/logic/useUnifiedEarnOpportunities'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { PillNav } from '@/ui/molecules/page-hero/PillNav'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { LayoutGrid, Sparkles } from 'lucide-react'

type BorrowViewMode = 'quick' | 'explore'

interface BorrowHubPageProps {
  defaultView?: BorrowViewMode
}

function formatUsd(value: number): string {
  return USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(value), { compact: true })
}

export function BorrowHubPage({ defaultView }: BorrowHubPageProps) {
  const viewMode: BorrowViewMode = defaultView ?? 'quick'
  const { aggregateMarketStats, isLoading } = useUnifiedEarnOpportunities()

  return (
    <PageLayout className="max-w-6xl gap-6 px-3">
      <PageHero
        title="Borrow"
        subtitle="Leverage your assets in seconds. Pick what you want to lend, choose what to borrow, and open your position."
        journey={['Select collateral', 'Pick borrow', '✨ Open position']}
        stats={[
          { label: 'Supplied', value: formatUsd(aggregateMarketStats.totalSuppliedUSD.toNumber()) },
          { label: 'Borrowed', value: formatUsd(aggregateMarketStats.totalBorrowedUSD.toNumber()) },
          { label: 'Available', value: formatUsd(aggregateMarketStats.totalAvailableUSD.toNumber()) },
        ]}
        statsLoading={isLoading}

        pillNav={
          <PillNav
            items={[
              { label: 'Quick', href: paths.borrow, icon: <Sparkles className="h-4 w-4" />, active: viewMode === 'quick' },
              { label: 'Explore', href: paths.marketsEBorrow, icon: <LayoutGrid className="h-4 w-4" />, active: viewMode === 'explore' },
            ]}
          />
        }
      />

      {viewMode === 'quick' && <EulerQuickBorrowContainer />}

      {viewMode === 'explore' && <EBorrow />}
    </PageLayout>
  )
}
